<template>
  <div class="mr-content">
    <template v-if="!isExternal">
      <div class="btn-group content-actions" v-if="hasMeasures">
        <button
          class="btn btn-info-outline btn-icon btn-icon-left"
          :class="{ 'btn-info': mode === 'measures' }"
          @click="mode = 'measures'"
        ><fa-icon :icon="['fal', 'monitor-heart-rate']"></fa-icon> Medidas</button>
        <button
          class="btn btn-info-outline btn-icon btn-icon-left"
          :class="{ 'btn-info': mode === 'report' }"
          @click="mode = 'report'"
        ><fa-icon :icon="['fal', 'line-height']"></fa-icon> Laudo</button>
      </div>

      <div v-if="hasMeasures" v-show="mode === 'measures'">
        <form class="form-horizontal measures">
          <div v-for="measure in measures" :key="measure.id" class="measure-group">
            <div class="measure-group-title">{{ measure.group }}</div>
            <div v-for="input in measure.inputs" class="form-group" :key="input.id">
              <div class="col-5">
                <label :for="input.id" class="form-label">{{ input.label }}</label>
              </div>
              <div class="col-3 input-group">
                <template v-if="input.type === 'formula'">
                  <dx-input-number
                    :value="input.value"
                    class="form-input text-right"
                    v-bind="input.params"
                    :disabled="!canEdit"
                    readonly="readonly"
                  />
                </template>
                <template v-else>
                  <dx-input-number
                    v-model="input.value"
                    class="form-input text-right"
                    v-bind="input.params"
                    @change="calculateFormulas"
                  />
                </template>
                <span class="input-group-addon">{{ input.unit }}</span>
              </div>
              <div class="col-4">
                <span class="ml-2">{{ input.params.info }}</span>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div v-if="hasInputs" v-show="mode === 'report'">
        <question
          v-for="input in inputs"
          v-model="input.model"
          :type="input.type"
          :label="input.label"
          :options="input.options"
          :params="input.params"
          :readonly="!canEdit"
          @input="save"
          :key="input.id"
        />
      </div>
    </template>

    <!-- EXTERNAL MEDICAL REPORT -->
    <template v-else>
      <div class="empty">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
        </div>
        <p class="empty-title h5">Laudo externo</p>
        <p class="empty-subtitle text-info">
          Este laudo foi importado de um programa externo e está disponível apenas para leitura.
        </p>
        <div class="empty-action">
          <a
            :href="data.externalReport"
            class="btn btn-info"
            target="_blank"
          >Visualizar Laudo</a>
        </div>
      </div>

      <div class="external-report-files">
        <div class="title h5">Arquivos complementares</div>

        <div class="upload-area mb-2" :class="{ dragging: upload.dragging }">
          <input
            type="file"
            accept=".pdf"
            class="input-file"
            @dragenter="upload.dragging = true"
            @dragleave="upload.dragging = false"
            @drop="upload.dragging = false"
            @change="uploadChange"
          >
          <div class="upload-inner">
            <fa-icon :icon="['fal', 'upload']"></fa-icon>
            <p>
              Arraste seus arquivos aqui<br>
              ou clique para selecionar
              <small class="text-info">(20mb max)</small>
            </p>
          </div>
        </div>

        <table class="table" v-if="files.length > 0">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Data</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in files" :key="item.id">
              <td>{{ item.name || item.id }}</td>
              <td>{{ item.date | date('datetime') }}</td>
              <td class="text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-icon btn-action btn-error"
                  :class="{ loading: item.processing }"
                  @click="removeFile(item)"
                ><fa-icon :icon="['fal', 'times']" /></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import form from '@/mixins/form';
import { Parser as ExprParser } from 'expr-eval';
import Question from './Question.vue';

export default {
  mixins: [form],
  props: {
    data: {
      id: {
        type: String,
      },
      templateId: {
        type: String,
      },
      inputs: {
        type: Array,
      },
      measures: {
        type: Array,
      },
      externalReport: {
        type: String,
      },
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isExternal: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Question,
  },
  data() {
    return {
      mode: 'report',
      templateId: null,
      inputs: [],
      measures: [],
      files: [],
      timeoutId: null,
      upload: {
        files: [],
        dragging: false,
      },
    };
  },
  mounted() {
    this.updateData();
  },
  watch: {
    'data.templateId': function dataTemplateId() {
      this.updateData();
    },
  },
  computed: {
    hasMeasures() {
      return this.measures && this.measures.length > 0;
    },
    hasInputs() {
      return this.inputs && this.inputs.length > 0;
    },
    measureInputs() {
      return this.hasMeasures
        ? this.measures.reduce((result, { inputs }) => ([
          ...result, ...inputs.filter(({ type }) => type !== 'formula'),
        ]), [])
        : [];
    },
    measureInputValues() {
      return this.measureInputs.reduce((result, { id, value }) => {
        result[id] = value;
        return result;
      }, {});
    },
  },
  methods: {
    updateData() {
      const {
        templateId, inputs, measures, files,
      } = this.data;

      this.templateId = templateId;

      this.inputs = Array.isArray(inputs)
        ? this.clone(inputs).map(item => ({
          ...item,
          model: {
            value: item.value || null,
            observation: item.observation || '',
          },
        }))
        : [];

      this.measures = Array.isArray(measures)
        ? this.clone(measures).map((group) => {
          group.inputs = group.inputs.map((input) => {
            input.value = input.value || 0;
            return input;
          });
          return group;
        })
        : [];

      const hasMeasuresToFill = (
        this.measureInputs.length > 0 && this.measureInputs.some(({ value }) => !value)
      );
      this.mode = hasMeasuresToFill ? 'measures' : 'report';

      this.files = Array.isArray(files)
        ? files.map(item => this.parseFileData(item))
        : [];
    },
    calculateFormulas() {
      this.save();
      this.measures.forEach((group) => {
        group.inputs.forEach((input) => {
          if (input.type === 'formula') {
            try {
              const formula = input.params.formula
                .replace(/{(.+?)}/g, (a, b) => (this.measureInputValues[b] || 0));
              input.value = ExprParser.evaluate(formula, this.measureInputValues) || 0;
              if (input.value === Infinity) {
                input.value = 0;
              }
            } catch (e) {
              input.value = 0;
            }
          }
        });
      });
    },
    save() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => {
        this.$emit('save', {
          templateId: this.templateId,
          inputs: this.inputs,
          measures: this.measures,
        });
      }, 600);
    },
    uploadChange(e) {
      e.preventDefault();
      if (e.target.files.length) {
        this.upload.files = e.target.files;

        Array.from(this.upload.files).forEach((file) => {
          const item = this.parseFileData({
            name: file.name,
            processing: true,
          });

          this.files.push(item);

          const formData = new FormData();
          formData.append('file', file);

          const path = `/medical-reports/${this.data.id}/files`;

          this.$http.post(path, formData)
            .then(({ data }) => {
              Object.assign(item, data, { processing: false });
            })
            .catch(() => {
              item.error = true;
              item.processing = false;
            });
        });
      } else {
        this.upload.files = [];
      }
    },
    async removeFile(item) {
      item.processing = true;
      try {
        await this.$http.delete(`/medical-reports/${this.data.id}/files/${item.id}`);
        this.files.splice(this.files.indexOf(item), 1);
      } catch (e) {
        this.$toast.error(e);
      }
    },
    parseFileData(data) {
      return {
        id: null,
        url: null,
        name: '',
        processing: false,
        date: null,
        error: false,
        ...data,
      };
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.mr-content {
  .measure-group {
    margin-bottom: $layout-spacing-lg;
    .measure-group-title {
      border-bottom: $border-width solid $border-color;
      font-weight: 600;
      padding-bottom: $layout-spacing-sm;
      margin-bottom: $layout-spacing;
      text-transform: uppercase;
    }
  }
  .measure-group:last-child {
    margin-bottom: 0;
  }

  .external-report-files {
    margin-top: $layout-spacing-lg;
    .title {
      font-weight: 500;
      color: $gray-color-dark;
    }
  }

  .upload-area {
    align-items: center;
    background-color: lighten($gray-color-light, 5%);
    border: lighten($gray-color, 5%) $unit-h dashed;
    display: flex;
    justify-content: center;
    margin-top: $layout-spacing;
    padding: $layout-spacing-lg 0;
    position: relative;
    &.dragging {
      border-color: $primary-color;
    }
    .input-file {
      display: block;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
    .upload-inner {
      display: flex;
      align-items: center;
    }
    svg {
      font-size: 1.8rem;
    }
    p {
      margin: 0 0 0 $layout-spacing-lg;
      text-align: center;
    }
  }
}
</style>
